import React, { useState, useEffect } from 'react';
import { Activity, AlertTriangle, CheckCircle, XCircle, Shield, Wrench, Clock, Calendar, RefreshCw, Zap, Database, HardDrive, Save } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HealthCheckPanel = ({ token }) => {
  const [healthData, setHealthData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSettings();
    runHealthCheck();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/health-check/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching health settings:', error);
    }
  };

  const runHealthCheck = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.get(`${API}/admin/health-check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealthData(response.data);
      setMessage({ type: 'success', text: 'Health check completed' });
    } catch (error) {
      console.error('Error running health check:', error);
      setMessage({ type: 'error', text: 'Failed to run health check' });
    } finally {
      setLoading(false);
    }
  };

  const fixAllIssues = async () => {
    if (!healthData) return;
    
    const fixableActions = healthData.issues
      .filter(i => i.auto_fixable && i.fix_action)
      .map(i => i.fix_action);
    
    if (fixableActions.length === 0) {
      setMessage({ type: 'info', text: 'No auto-fixable issues found' });
      return;
    }
    
    if (!window.confirm(`This will attempt to fix ${fixableActions.length} issue(s). Continue?`)) return;
    
    setFixing(true);
    try {
      const response = await axios.post(`${API}/admin/health-check/fix`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { fix_actions: fixableActions.join(',') }
      });
      setMessage({ type: 'success', text: `Fixed ${response.data.fixed_count} issue(s)` });
      // Re-run health check to see updated status
      await runHealthCheck();
    } catch (error) {
      console.error('Error fixing issues:', error);
      setMessage({ type: 'error', text: 'Failed to fix some issues' });
    } finally {
      setFixing(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const params = new URLSearchParams({
        weekly_auto_check: settings.weekly_auto_check,
        email_alerts: settings.email_alerts || false,
        alert_email: settings.alert_email || ''
      });
      
      await axios.put(`${API}/admin/health-check/settings?${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchSettings();
      setMessage({ type: 'success', text: 'Health check settings saved' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSavingSettings(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle size={18} className="status-icon healthy" />;
      case 'warning':
        return <AlertTriangle size={18} className="status-icon warning" />;
      case 'critical':
        return <XCircle size={18} className="status-icon critical" />;
      default:
        return <Activity size={18} />;
    }
  };

  const getOverallStatusClass = () => {
    if (!healthData) return '';
    return `overall-${healthData.overall_status}`;
  };

  return (
    <div className="settings-card health-check-section" data-testid="health-check-panel">
      <h3><Activity size={18} /> Website Health Check</h3>
      <p className="section-description">Monitor and fix critical issues for smooth website operation</p>
      
      {message.text && (
        <div className={`settings-message ${message.type}`}>{message.text}</div>
      )}

      {/* Persistent Last Run & Next Scheduled Info */}
      {settings && (settings.last_check || settings.next_scheduled_check) && (
        <div className="health-check-schedule-info" data-testid="health-schedule-info" style={{ display: 'flex', gap: '2rem', padding: '0.75rem 1rem', background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {settings.last_check && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={16} style={{ color: '#718096' }} />
              <span style={{ fontSize: '0.9rem', color: '#4a5568' }}>
                <strong>Last Run:</strong> {new Date(settings.last_check).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} at {new Date(settings.last_check).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}
          {settings.weekly_auto_check && settings.next_scheduled_check && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={16} style={{ color: '#38a169' }} />
              <span style={{ fontSize: '0.9rem', color: '#4a5568' }}>
                <strong>Next Scheduled:</strong> {new Date(settings.next_scheduled_check).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            </div>
          )}
          {settings.last_auto_result && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {settings.last_auto_result.overall_status === 'healthy' ? 
                <CheckCircle size={16} style={{ color: '#38a169' }} /> : 
                <AlertTriangle size={16} style={{ color: '#d69e2e' }} />
              }
              <span style={{ fontSize: '0.9rem', color: '#4a5568' }}>
                <strong>Auto-check:</strong> {settings.last_auto_result.overall_status === 'healthy' ? 'All clear' : 'Issues found'}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Health Check Actions */}
      <div className="health-check-actions">
        <Button 
          onClick={runHealthCheck} 
          disabled={loading} 
          className="run-check-btn"
          data-testid="run-health-check-btn"
        >
          <Zap size={16} /> {loading ? 'Checking...' : 'Run Health Check'}
        </Button>
        
        {healthData && healthData.issues.some(i => i.auto_fixable) && (
          <Button 
            onClick={fixAllIssues} 
            disabled={fixing} 
            className="fix-all-btn"
            data-testid="fix-all-issues-btn"
          >
            <Wrench size={16} /> {fixing ? 'Fixing...' : 'Fix All Issues'}
          </Button>
        )}
      </div>

      {loading && !healthData && (
        <p style={{ color: '#718096', padding: '1rem 0' }}>Running health check...</p>
      )}

      {/* Health Status Overview */}
      {healthData && (
        <div className={`health-status-overview ${getOverallStatusClass()}`} data-testid="health-status-overview">
          <div className="health-status-header">
            {getStatusIcon(healthData.overall_status)}
            <span className="status-text">
              {healthData.overall_status === 'healthy' ? 'All Systems Healthy' :
               healthData.overall_status === 'warning' ? 'Some Issues Found' :
               'Critical Issues Detected'}
            </span>
          </div>
          <div className="health-stats">
            <div className="health-stat healthy">
              <CheckCircle size={16} />
              <span>{healthData.healthy_count} Healthy</span>
            </div>
            <div className="health-stat warning">
              <AlertTriangle size={16} />
              <span>{healthData.warning_count} Warnings</span>
            </div>
            <div className="health-stat critical">
              <XCircle size={16} />
              <span>{healthData.critical_count} Critical</span>
            </div>
          </div>
          {healthData.last_check && (
            <div className="last-check-time">
              <p>Last Run: {new Date(healthData.last_check).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')}</p>
              {settings?.weekly_auto_check && (
                <p>Next Scheduled: {(() => {
                  const last = new Date(healthData.last_check);
                  const next = new Date(last.getTime() + 7 * 24 * 60 * 60 * 1000);
                  return next.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
                })()}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Issues List */}
      {healthData && healthData.issues && (
        <div className="health-issues-list" data-testid="health-issues-list">
          <h4>Diagnostic Results</h4>
          {healthData.issues.map((issue, index) => (
            <div key={index} className={`health-issue-item ${issue.status}`}>
              <div className="issue-header">
                {getStatusIcon(issue.status)}
                <span className="issue-category">{issue.category}</span>
                {issue.auto_fixable && (
                  <span className="auto-fixable-badge">Auto-fixable</span>
                )}
              </div>
              <p className="issue-message">{issue.message}</p>
              {issue.details && (
                <div className="issue-details">
                  {Object.entries(issue.details).map(([key, value]) => (
                    <span key={key} className="detail-item">
                      {key.replace(/_/g, ' ')}: {value}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Auto Check Settings */}
      {settings && (
        <div className="health-check-settings">
          <h4><Clock size={16} /> Automated Health Checks</h4>
          <div className="settings-form">
            <div className="field checkbox-field">
              <input 
                type="checkbox"
                id="weekly-auto-check"
                checked={settings.weekly_auto_check}
                onChange={(e) => setSettings({...settings, weekly_auto_check: e.target.checked})}
                data-testid="weekly-auto-check-toggle"
              />
              <label htmlFor="weekly-auto-check">Enable Weekly Auto Health Check</label>
            </div>
            {settings.weekly_auto_check && settings.next_scheduled_check && (
              <p className="next-check-info">
                Next scheduled check: {new Date(settings.next_scheduled_check).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            )}
            {settings.weekly_auto_check && !settings.next_scheduled_check && (
              <p className="next-check-info">
                Save settings to schedule the first auto check.
              </p>
            )}
            <Button 
              onClick={handleSaveSettings} 
              disabled={savingSettings} 
              className="save-btn"
            >
              <Save size={16} /> {savingSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


// Form Field Editor Component for Design Journey

export { HealthCheckPanel };
